import React, { useState } from "react";
import { useQuery, useMutation, gql } from "@apollo/client";

// Adds someone left out of a month that is already locked (added to the
// system late, switched off by mistake, dates fixed after the lock). Only
// people employed that month with no payout in it are listed, with what
// they'd get - the same calculation as the monthly payroll.

const GET_CANDIDATES = gql`
  query GetSupplementaryCandidates($payrollRunId: ID!) {
    getSupplementaryCandidates(payrollRunId: $payrollRunId) {
      status
      message
      candidates {
        employeeId
        fullName
        activeDays
        salary
        commission
        bookings
        leaveDeduction
        adjustmentTotal
        net
        carriedForward
      }
    }
  }
`;

const ADD = gql`
  mutation AddSupplementaryPayout($payrollRunId: ID!, $employeeId: ID!, $reason: String!) {
    addSupplementaryPayout(payrollRunId: $payrollRunId, employeeId: $employeeId, reason: $reason) {
      status
      message
    }
  }
`;

const inr = (n) => `₹${Math.round(Number(n) || 0).toLocaleString("en-IN")}`;

const AddSupplementaryModal = ({ run, monthLabel, onClose, onAdded }) => {
  const { data, loading } = useQuery(GET_CANDIDATES, {
    variables: { payrollRunId: run.id },
    fetchPolicy: "network-only",
  });
  const [add, { loading: saving }] = useMutation(ADD);
  const [picked, setPicked] = useState(null);
  const [reason, setReason] = useState("");
  const res = data?.getSupplementaryCandidates;
  const candidates = res?.candidates || [];

  const submit = async () => {
    try {
      const out = (await add({ variables: { payrollRunId: run.id, employeeId: picked.employeeId, reason } })).data.addSupplementaryPayout;
      alert(out.message);
      if (out.status === "SUCCESS") onAdded();
    } catch (err) {
      alert(err.message);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg p-6 w-[720px] max-w-[95vw] shadow-xl max-h-[85vh] overflow-y-auto">
        <h3 className="text-lg font-bold text-gray-800">Add a missed professional to {monthLabel}</h3>
        <p className="text-xs text-gray-500 mt-1 mb-4">
          For someone left out of this locked month. Their pay is worked out the same way as everyone else's.
          With maker-checker on, another admin approves it before it can be paid.
        </p>

        {loading ? (
          <p className="text-sm text-gray-500">Working out who is missing...</p>
        ) : res?.status === "ERROR" ? (
          <p className="text-sm text-red-600">{res.message}</p>
        ) : candidates.length === 0 ? (
          <p className="text-sm text-gray-500">
            Nobody is missing - everyone employed in {monthLabel} is in this payroll. If someone should be here,
            check their Employed status and joining / leaving dates in User Management first.
          </p>
        ) : (
          <table className="min-w-full text-left text-sm text-gray-600 border rounded">
            <thead className="bg-gray-50 text-gray-700 text-xs font-bold border-b">
              <tr>
                <th className="py-2 px-3" />
                <th className="py-2 px-3">Professional</th>
                <th className="py-2 px-3">Days</th>
                <th className="py-2 px-3">Salary</th>
                <th className="py-2 px-3">Commission</th>
                <th className="py-2 px-3">Leave</th>
                <th className="py-2 px-3">Adj.</th>
                <th className="py-2 px-3">Net</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {candidates.map((c) => (
                <tr
                  key={c.employeeId}
                  onClick={() => setPicked(c)}
                  className={`cursor-pointer ${picked?.employeeId === c.employeeId ? "bg-blue-50" : "hover:bg-gray-50"}`}
                >
                  <td className="py-2 px-3">
                    <input type="radio" readOnly checked={picked?.employeeId === c.employeeId} />
                  </td>
                  <td className="py-2 px-3 text-gray-800">{c.fullName}</td>
                  <td className="py-2 px-3">{c.activeDays}</td>
                  <td className="py-2 px-3">{inr(c.salary)}</td>
                  <td className="py-2 px-3">
                    {inr(c.commission)} <span className="text-xs text-gray-400">({c.bookings})</span>
                  </td>
                  <td className="py-2 px-3 text-red-600">{c.leaveDeduction ? `-${inr(c.leaveDeduction)}` : "-"}</td>
                  <td className="py-2 px-3">{c.adjustmentTotal ? inr(c.adjustmentTotal) : "-"}</td>
                  <td className="py-2 px-3 font-semibold">
                    {inr(c.net)}
                    {c.carriedForward > 0 && (
                      <div className="text-[11px] font-normal text-red-600">{inr(c.carriedForward)} to next month</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {picked && (
          <div className="mt-4">
            <label className="block text-sm font-semibold text-gray-700 mb-1">Why were they left out?</label>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Added to the system after the lock"
              className="w-full border rounded px-3 py-2 text-sm"
            />
          </div>
        )}

        <div className="flex justify-end gap-2 mt-5">
          <button onClick={onClose} className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded text-sm">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!picked || reason.trim().length < 5 || saving}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-semibold disabled:opacity-40"
          >
            {saving ? "Adding..." : picked ? `Add ${picked.fullName} (${inr(picked.net)})` : "Add"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddSupplementaryModal;
